import { useEffect, useRef, useState, useCallback } from "react";
import { authApi } from "@/lib/api";

/**
 * Connects to /api/ws/fleet via WebSocket using a short-lived token.
 * Returns { assets, alerts, connected }.
 */
export function useFleetWebSocket(initialAssets = []) {
  const [assets, setAssets] = useState(initialAssets);
  const [alertEvents, setAlertEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const [lastTick, setLastTick] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const stoppedRef = useRef(false);

  const connect = useCallback(async () => {
    if (stoppedRef.current) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    let token;
    try {
      token = await authApi.wsToken();
    } catch (e) {
      // Auth missing or refresh failed — schedule retry. Surface in dev tools
      // so 401 loops are visible without breaking the silent reconnect flow.
      console.warn("[useFleetWebSocket] wsToken fetch failed; retrying in 5s", e);
      if (!reconnectRef.current && !stoppedRef.current) {
        reconnectRef.current = setTimeout(() => {
          reconnectRef.current = null;
          connect();
        }, 5000);
      }
      return;
    }
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/fleet?token=${encodeURIComponent(token)}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "snapshot" || msg.type === "tick") {
            if (Array.isArray(msg.assets)) setAssets(msg.assets);
            setLastTick(new Date());
          }
          if (msg.type === "tick" && Array.isArray(msg.alerts) && msg.alerts.length > 0) {
            setAlertEvents((prev) => [...msg.alerts, ...prev].slice(0, 50));
          }
        } catch (err) {
          // Malformed WS payload; log and continue so a single bad frame
          // doesn't tear down the socket.
          console.warn("[useFleetWebSocket] failed to parse WS frame", err);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (!reconnectRef.current && !stoppedRef.current) {
          reconnectRef.current = setTimeout(() => {
            reconnectRef.current = null;
            connect();
          }, 2500);
        }
      };

      ws.onerror = () => {
        try { ws.close(); } catch (_e) { /* WS already torn down */ }
      };
    } catch (err) {
      console.warn("[useFleetWebSocket] WebSocket constructor threw", err);
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    connect();
    return () => {
      stoppedRef.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_e) { /* WS already closed */ }
      }
    };
  }, [connect]);

  return { assets, setAssets, alertEvents, connected, lastTick };
}
